import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { MotionHighlight } from '@/components/animate-ui'
import { useAccessibleMotion } from '@/hooks/useAccessibleMotion'
import { GlobalSearch, MobileSearchModal } from '@/components/search'
import { 
  Menu, 
  Bell, 
  User, 
  LogOut, 
  Search,
  Plus,
  HelpCircle
} from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'

interface HeaderProps {
  onMenuClick: () => void
  isMenuCollapsed: boolean
}

export default function Header({ onMenuClick }: HeaderProps) {
  const { user, signOut } = useAuth()
  const { prefersReducedMotion, slideUp } = useAccessibleMotion()
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  
  return (
    <motion.header 
      className="bg-card border-b px-6 py-3"
      {...slideUp}
    >
      <div className="flex items-center justify-between">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <MotionHighlight>
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className="md:hidden"
              aria-label="Toggle menu"
            >
              <motion.div
                {...(!prefersReducedMotion && {
                  whileHover: { rotate: 180 },
                  transition: { duration: 0.3 }
                })}
              >
                <Menu className="w-4 h-4" aria-hidden="true" />
              </motion.div>
            </Button>
          </MotionHighlight>
          
          {/* Breadcrumb */}
          <motion.nav 
            className="hidden md:flex items-center space-x-2 text-sm text-muted-foreground"
            initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.4, delay: prefersReducedMotion ? 0 : 0.1 }}
          >
            <span>Dashboard</span>
            <span>/</span>
            <span className="text-foreground">Overview</span>
          </motion.nav>
        </div>

        {/* Center section - Global Search */}
        <motion.div 
          className="hidden md:flex items-center flex-1 max-w-md mx-8"
          initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: prefersReducedMotion ? 0.01 : 0.4, delay: prefersReducedMotion ? 0 : 0.2 }}
        >
          <MotionHighlight
            highlightClassName="bg-primary/5 border border-primary/20"
            className="w-full rounded-lg"
          >
            <GlobalSearch 
              className="w-full"
              placeholder="Search galleries, services, leads..."
            />
          </MotionHighlight>
        </motion.div>

        {/* Right section */}
        <motion.div 
          className="flex items-center gap-2"
          initial={{ opacity: 0, x: prefersReducedMotion ? 0 : 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.01 : 0.4, delay: prefersReducedMotion ? 0 : 0.3 }}
        >
          {/* Mobile Search Button */}
          <MotionHighlight>
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden" 
              aria-label="Search"
              onClick={() => setShowMobileSearch(true)}
            >
              <motion.div
                {...(!prefersReducedMotion && {
                  whileHover: { scale: 1.1 },
                  transition: { duration: 0.2 }
                })}
              >
                <Search className="w-4 h-4" aria-hidden="true" />
              </motion.div>
            </Button>
          </MotionHighlight>

          {/* Quick Actions */}
          <MotionHighlight>
            <Button variant="ghost" size="icon" className="hidden md:flex" aria-label="Create new">
              <motion.div
                {...(!prefersReducedMotion && {
                  whileHover: { rotate: 90, scale: 1.1 },
                  transition: { duration: 0.2 }
                })}
              >
                <Plus className="w-4 h-4" aria-hidden="true" />
              </motion.div>
            </Button>
          </MotionHighlight>
          
          <MotionHighlight>
            <Button variant="ghost" size="icon" className="hidden md:flex" aria-label="Help">
              <motion.div
                {...(!prefersReducedMotion && {
                  whileHover: { scale: 1.1 },
                  transition: { duration: 0.2 }
                })}
              >
                <HelpCircle className="w-4 h-4" aria-hidden="true" />
              </motion.div>
            </Button>
          </MotionHighlight>

          {/* Notifications */}
          <MotionHighlight>
            <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
              <motion.div
                {...(!prefersReducedMotion && {
                  whileHover: { rotate: 15, scale: 1.1 },
                  transition: { duration: 0.2 }
                })}
              >
                <Bell className="w-4 h-4" aria-hidden="true" />
              </motion.div>
              <motion.span 
                className="absolute -top-1 -right-1 w-3 h-3 bg-destructive rounded-full text-xs text-destructive-foreground flex items-center justify-center"
                {...(!prefersReducedMotion && {
                  animate: { scale: [1, 1.2, 1] },
                  transition: { duration: 2, repeat: Infinity, repeatDelay: 3 }
                })}
              >
                3
              </motion.span>
            </Button>
          </MotionHighlight>

          {/* User Menu */}
          <div className="flex items-center gap-2 pl-2">
            <MotionHighlight>
              <Button variant="ghost" className="h-8 px-2 gap-2">
                <motion.div 
                  className="w-6 h-6 bg-primary rounded-full flex items-center justify-center"
                  {...(!prefersReducedMotion && {
                    whileHover: { scale: 1.1, rotate: 5 },
                    transition: { duration: 0.2 }
                  })}
                >
                  <User className="w-3 h-3 text-primary-foreground" aria-hidden="true" />
                </motion.div>
                <span className="hidden md:block text-sm">{user?.name || user?.email || 'User'}</span>
              </Button>
            </MotionHighlight>
            
            {/* Logout Button */}
            <MotionHighlight>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => signOut()}
                title="Sign out"
                aria-label="Sign out"
              >
                <motion.div
                  {...(!prefersReducedMotion && {
                    whileHover: { scale: 1.1 },
                    whileTap: { scale: 0.9 },
                    transition: { duration: 0.1 }
                  })}
                >
                  <LogOut className="w-4 h-4" aria-hidden="true" />
                </motion.div>
              </Button>
            </MotionHighlight>
          </div>
        </motion.div>
      </div>

      {/* Mobile Search Modal */}
      <MobileSearchModal 
        isOpen={showMobileSearch}
        onClose={() => setShowMobileSearch(false)}
      />
    </motion.header>
  )
}